<template>
  <div>
    <div class="form-container start-block" :class="{ fishersci: isFishersci }">
      <div class="info-container">
        <p>{{ getInfoMessage }}<br/><i v-if="showMandatory">{{ $t('mandatory') }}</i></p>
      </div>
      <form @submit.prevent="submitForm" @keydown.enter="submitForm">
      <div v-for="(input, index) in activePreChatFields" :key="index" class="form-field">
        <div v-if="input.fieldType === 'SELECT'" class="select-wrap">
          <!-- Check if the input type is SELECT -->
          <select :id="input.fieldName"
                  :name="input.fieldName"
                  :disabled="disabled"
                  v-model="formData[input.fieldName]"
                  @change="validateField(input)"
                  class="field-input"
          >
          <optgroup :label="input.displayName"></optgroup>
            <option v-for="(option, optionIndex) in input.values" :key="optionIndex" :value="option.value">{{ option.label}}</option>
          </select>
          <span v-if="!formData[input.fieldName]" class="placeholder">{{ input.displayName + (input.isMandatory ? ' *' : '') }}</span>
        </div>

        <div v-else-if="false">
          <vue-tel-input
              v-model.trim="formData[input.fieldName]"
              :inputOptions="{ placeholder: input.displayName + (input.isMandatory ? ' *' : ''), required: input.isMandatory, showDialCode: true }"
              :disabled="disabled"
              maxLen="16"
              @input="handlePhoneInput($event, input)"
              @validate="checkPhone($event, input)"
              class="field-input"
          />
        </div>

        <div v-else-if="input.fieldType.includes('PHONE_')">
          <vue-tel-input
              v-model.trim="formData[input.fieldName]"
              :inputOptions="{ placeholder: input.displayName + (input.isMandatory ? ' *' : ''), required: input.isMandatory, showDialCode: true }"
              :disabled="disabled"
              :onlyCountries="getCountryOptions(input)"
              @input="handlePhoneInput($event, input)"
              @validate="checkPhone($event, input)"
              class="field-input"
          />
        </div>
        <div v-else-if="input.fieldType === 'TEXTAREA'">
          <textarea
          :id="input.fieldName"
          :disabled="disabled"
          :name="input.fieldName"
          v-model.trim="formData[input.fieldName]"
          :placeholder="input.displayName + (input.isMandatory ? ' *' : '')"
          @input="validateField(input)"
          @blur="validateField(input)"
          maxlength='500'
         >
          </textarea>
        </div>
        <!-- Check if the input type is INPUT -->

        <input v-else
               :type="getFieldType(input.fieldType)"
               :id="input.fieldName"
               :disabled="disabled"
               :name="input.fieldName"
               :placeholder ="input.displayName +  (input.isMandatory ? ' *' : '')"
               v-model.trim="formData[input.fieldName]"
               @input="validateField(input)"
               @blur="validateField(input)"
               class="field-input"
        />

        <span v-if="countryCode !=='kr'" class="error" :class="{ tfErrorText: !isFishersci }" v-show="errors[input.fieldName]">{{ errors[input.fieldName] }}</span>
      </div>
      <span v-if="countryCode ==='kr' && Object.values(errors).some(e => e)" :class="{ tfErrorText: !isFishersci }" class="error">{{$t('ValidationMsgKr')}}</span>
     </form>
      <span class="privacy-message" v-html="privacyDisclaimerMessage"></span>
      <button
        v-if="countryCode === 'kr'"
        class="submit-button"
        @click="submitForm"
        :disabled="disabled || !isFormValid"
      >
      <span v-if="disabled">{{ $t('submitkr') }}</span>
      <span  v-else class="submit-label">{{ $t('submitkr') }}</span>
      </button>
      <button
        v-else
        class="submit-button"
        @click="submitForm"
        :disabled="disabled || !isFormValid"
      >
        <span v-if="disabled">Submitted</span>
        <span v-else>{{ $t('submit') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import userAdditionaldata from '../../mixins/userAdditionaldata'
import countryList from '@/config/counriesList.json'
import { mapState } from 'pinia'
import { useWidgetStore } from '@/store'
import { uiToggleConfigs } from '@/utils/constants'

export default {
  mixins: [userAdditionaldata],
  props: {
    prechatInputs: {
      type: Array,
      default: () => ([])
    },
    modelData: {
      type: Object,
      default: () => ({})
    },
    privacyDisclaimerMessage: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isMidChatForm: {
      type: Boolean,
      default: false
    },
    userDetails: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      formData: {},
      errors: {},
      countryList: countryList,
      phone: '',
      countryCode: '' // Initialize as empty string
    }
  },
  created() {
    this.countryCode = this.getCountryCode()
  },
  computed: {
    ...mapState(useWidgetStore, ['brandingColor', 'isFishersci']),
    activePreChatFields () {
      return this.prechatInputs.filter(input => !input.bidSalesAgentOffline)
    },
    hasOfflineAgentField () {
      return this.prechatInputs.some(input => input.bidSalesAgentOffline)
    },
    isFormValid () {
      // Check if all mandatory fields are filled and valid
      for (const input of this.prechatInputs) {
        if (input.isMandatory) {
          const value = this.formData[input.fieldName]
          // Check if field is empty
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            return false
          }
          // Check if field has validation errors
          if (this.errors[input.fieldName]) {
            return false
          }
        }
      }
      // Check if there are any validation errors at all
      return !Object.values(this.errors).some(error => error)
    },
    getInfoMessage () {
      if (this.countryCode === 'kr' && this.hasOfflineAgentField) {
        return this.$t('provideInfoKrAgentOffline')
      } else if (this.countryCode === 'kr') {
        return this.$t('provideInfoKr')
      } else if (this.hasOfflineAgentField) {
        return this.$t('provideInfoAgentOffline')
      } else {
        return this.$t('provideInfo')
      }
    },
    showMandatory () {
      // Show mandatory label for all except KR without agent offline
      return !(this.countryCode === 'kr' && !this.hasOfflineAgentField)
    }
  },
  mounted () {
    for (const input of this.prechatInputs) {
      if (input.defaultValue) {
        this.formData[input.fieldName] = input.defaultValue
      }
    }
    if (this.modelData && Object.keys(this.modelData).length > 0) {
      this.formData = Object.assign(this.modelData)
    }

    if (this.isFishersci) {
      // Pre-populating the midchat form, prechat form and open array form  details for logged in users in Fishersci
      if (window?.fisherDataLayer && window?.fisherDataLayer.userData && window?.fisherDataLayer.userData.loggedIn === 'Yes') {
        this.formData.firstName = window?.fisherDataLayer.userData?.firstName
        this.formData.lastName = window?.fisherDataLayer.userData?.lastName
        this.formData.email = window?.fisherDataLayer.userData?.userEmail
        this.formData.phoneNumber = window?.fisherDataLayer.userData?.phone
        this.formData.accountNumber = parseInt(window?.fisherDataLayer.userData?.account) ? window?.fisherDataLayer.userData?.account : null
      }
    } else if (window.location.href.includes('ampliseq')) {
      if (this.userDetails.userDetails.length > 0) {
        this.formData.firstName = this.userDetails.userDetails[0]?.firstName
        this.formData.lastName = this.userDetails.userDetails[0]?.lastName
        this.formData.email = this.userDetails.userDetails[0]?.userMail
        if (this.userDetails.userDetails[0]?.userPhoneNumber) {
          this.formData.phone = this.userDetails.userDetails[0]?.userPhoneNumber
        }
      }
    } else {
      // Pre-populating the midchat form, prechat form and open array form  details for logged in users in Thermofisher
      if (this.userDetails?.data && uiToggleConfigs?.enableLoggedInUser && !this.userDetails?.data.isAnonymous) {
        this.formData.firstName = this.userDetails?.data?.firstName
        this.formData.lastName = this.userDetails?.data?.lastName
        this.formData.email = this.userDetails?.data?.email
        this.formData.phone = this.userDetails?.data?.phone
      } else {
        window._lt = window._lt || {}
        if (window._lt && window._lt.user && window._lt.user.isLoggedIn && window._lt.user.info) {
          this.formData.firstName = window._lt.user.info.firstName
          this.formData.lastName = window._lt.user.info.lastName
          this.formData.email = window._lt.user.info.email
          this.formData.phone = window._lt.user.info.phone
        }
      }
    }
  },
  methods: {
     getCountryCode () {
    return localStorage.getItem('country_code').toLowerCase() || ''
  },
    submitForm () {
      if (this.disabled) {
        return
      }
      // Validate all fields before submission
      for (const input of this.prechatInputs) {
        if (input.fieldType === 'PHONE') {
          this.validatePhone(this.formData[input.fieldName], input)
        } else if (input.fieldType.includes('PHONE_')) {
          this.validatePhoneXX(this.formData[input.fieldName], input)
        } else {
          this.validateField(input)
        }
      }

      // Check if there are any errors
      for (const input of this.prechatInputs) {
        if (this.errors[input.fieldName]) {
          return
        }
      }

      if (!this.isMidChatForm) {
        // Add additional parameters to the form data for genesys
        this.addAdditionsParameters()
        this.formData.genesysOS = this.getUserOS()
        this.formData.genesysBrowser = this.getBrowser()
        this.formData.customerLastURLs = this.userRecentURLS()

        // Capitalize first letter of first name
        this.formData.firstName = this.capitalizeFirstLetter(this.formData.firstName)
      }

      this.$emit('submit', { form: this.formData })
    },
    validateField (input) {
      const value = this.formData[input.fieldName]
      const number = typeof value === 'number'

      if (this.errors[input.fieldName]) {
        this.errors[input.fieldName] = ''
      }

      if (input.fieldType === 'SELECT') {
        // Check if the selected option is empty and marked as mandatory
        if (input.isMandatory && (!value || value.trim() === '' || value === '')) {
          this.errors[input.fieldName] = `${input.displayName} is required`
        }
      } else if (number) {
        if (input.fieldType === 'INTEGER' && !/^\d+$/.test(value)) {
          this.errors[input.fieldName] = `${input.displayName} must be a valid integer`
        }
      } else if (input.regex) {
        const pattern = new RegExp(input.regex)
        if (!pattern.test(value)) {
          this.errors[input.fieldName] = 'The value does not match the pattern.'
        }
      } else {
        // Handle other input types
        if ((!value || value.trim() === '') && input.isMandatory) {
          if (this.countryCode === 'kr') {
            this.errors[input.fieldName] = this.$i18n.t('ValidationMsgKr')
          } else {
            this.errors[input.fieldName] = this.$i18n.t(input.fieldName + 'ReqVld')
          }
        } else if (input.fieldType === 'WORD' && (value && value?.trim() !== '') && !/^\S+$/.test(value)) {
          this.errors[input.fieldName] = `${input.displayName}` + ' ' + this.$i18n.t('worldErrorMsg')
        } else if (input.fieldType === 'PHONE' && (value && value?.trim() !== '') && !/^(\+)?[\d\s\-]{10,16}$/.test(value.replace(/[\s\-]/g, ''))) {
          this.errors[input.fieldName] = this.$i18n.t('phoneErrorMsg')
        } else if (input.fieldType === 'EMAIL' && (value && value?.trim() !== '') && !/^(([^<>()\\[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value)) {
          this.errors[input.fieldName] = this.$i18n.t('emailErrorMsgValidation')
        }
      }
    },
    validatePhone (model, input) {
      if (!model && input.isMandatory) {
        this.errors[input.fieldName] = `${input.displayName} is required`
      } else if (model) {
        // Check if the phone number contains parentheses
        if (model.includes('(') || model.includes(')')) {
          this.errors[input.fieldName] = `Invalid format for ${input.displayName} (between 10 and 16 digits required)`
        } else if (!/^(\+)?[\d\s\-]{10,16}$/.test(model.replace(/[\s\-]/g, ''))) {
          this.errors[input.fieldName] = this.$i18n.t('phoneErrorMsg')
        } else {
          const cleanedValue = model.replace(/[\s\-\+]/g, '')
          if (cleanedValue.length < 10 || cleanedValue.length > 16) {
            this.errors[input.fieldName] = `Invalid format for ${input.displayName} (between 10 and 16 digits required)`
          } else {
            this.errors[input.fieldName] = ''
          }
        }
      } else {
        this.errors[input.fieldName] = ''
      }
    },
    checkPhone (data, input) {
      if (input.isMandatory && !data?.number && !data.valid) {
        this.errors[input.fieldName] = `${input.displayName} is required`
      }
      if (!data.valid && data?.number) {
        this.errors[input.fieldName] = `Invalid format for ${input.displayName}`
      } else {
        this.errors[input.fieldName] = ''
      }
    },
    handlePhoneInput (event, input) {
      // Preserve the + sign if it exists
      const inputValue = event.target.value
      const formattedInput = inputValue.startsWith('+') ? '+' : ''

      // Remove non-numeric characters (excluding the + sign)
      const numericValue = inputValue.replace(/\D/g, '')

      // Concatenate the preserved + sign and numeric characters
      this.formData[input.fieldName] = formattedInput + numericValue
    },
    validatePhoneXX (value, input) {
      this.errors[input.fieldName] = ''
      if (!value && input.isMandatory) {
        this.errors[input.fieldName] = `${input.displayName} is required`
      } else if (value) {
        const country = this.countryList.find(i => input?.fieldType.slice(-2).toLowerCase() === i.code.toLowerCase())
        if (!value.startsWith(country.dial_code)) {
          this.errors[input.fieldName] = `Invalid format for ${input.displayName} (E164 format with country code ${input?.fieldType.slice(-2)} required)`
        } else if (value.length < 11 || value.length > 18) {
          this.errors[input.fieldName] = `Invalid format for ${input.displayName} (between 10 and 16 digits)`
        }
      }
    },
    getFieldType (fieldType) {
      // Map fieldType to input type
      if (fieldType === 'PHONE' || fieldType.includes('PHONE_')) {
        return 'tel'
      } else if (fieldType === 'EMAIL') {
        return 'email'
      } else if (fieldType === 'INTEGER') {
        return 'number'
      }
      return 'text'
    },
    getCountryOptions (input) {
      return [input?.fieldType.slice(-2).toLowerCase()]
    },
    capitalizeFirstLetter (string) {
      if (!string) {
        return
      }
      return string.charAt(0).toUpperCase() + string.slice(1)
    }
  },
}
</script>

<style scoped lang="sass">

@import url('https://unpkg.com/vue-tel-input@8.2.0/dist/vue-tel-input.css')
.info-container
  padding: 0px 8px
  border-radius: 8px
  color: #253339
  border: 1px solid #e5eaed
  background-color: #f7f7f7
  margin-bottom: 8px
.start-block
  display: flex
  flex-direction: column
  h2
    font-size: 20px
    font-weight: 500
    color: #222222
    line-height: 33px
    margin-bottom: 0
    margin-top: 9px

  p
    font-size: 12px
    color: #767676
    margin-bottom: 8px
    margin-top: 8px
    line-height: 16px
  i
    font-size: 10px
  .select-wrap
    position: relative
  optgroup
    font-weight: lighter
    color: gray
    font-size: 12px
  .field-input
    height: 32px
    width: 100%
    border: 1px solid #C9C8C8
    border-radius: 5px
    outline: none
    padding: 7px 9px
    font-size: 12px
    color: #222222
    margin-bottom:0px

  textarea
    width:100%
    height:100px
    border: 1px solid #C9C8C8
    border-radius:5px
    color: #222222
    font-size:12px
    outline: none
    resize: none
    padding: 7px 9px
    font-family: inherit
    font-weight:400
    box-shadow: none
    max-width: 100%

    &:focus-visible
     outline: none

    &:focus
     box-shadow: none

    &:disabled
      background-color: #eee

  .field-input[type="number"]::-webkit-inner-spin-button
    -webkit-appearance: none

  .placeholder
    position: absolute
    left: 12px
    top: 50%
    transform: translateY(-50%)
    pointer-events: none
    transition: 0.3s ease-out
    color: #565656
    font-size: 12px
    background: none
    box-shadow: none

  .privacy-message
    font-size: 12px
    line-height: 16px
    color: #767676

    :deep(a)
      color: #1e8ae7 !important
      text-decoration: none

  .submit-button
    cursor: pointer
    border: none
    height: 32px
    padding: 8px 18px
    margin: 8px 8px 8px auto
    background-color: v-bind('brandingColor')
    color: #fff
    font-size: 12px
    font-weight: 700
    border-radius: 3px
    &:disabled
      background-color: #ccc
      pointer-events: none

    &:hover
      opacity: 0.8
  .submit-button
    &:disabled .submit-label
      background-color: #ccc
      pointer-events: none

    .submit-label:hover
      opacity: 0.8

.form-container
  padding: 0 8px
  max-width: 320px
  margin: 0 auto

  .error
    color: red
    font-size: 11px
    margin: 5px 0
    display: block
    line-height:12px

  .error.tfErrorText
    color: #f2171c

.form-field
  margin-bottom: 8px

.field-label
  font-size: 16px
  display: block
  margin-bottom: 5px

:deep(.vti__dropdown-list)
  max-width: 330px

.field-input::placeholder
  font-size: 12px
  color: #565656
.fishersci
  .info-container
    margin-bottom: 6px
    p
     font-size: 11px !important
     margin-bottom: 6px
     color: #444444
     line-height: 14px
    i
     font-size:9px
  .field-input
    height: 30px
  .form-field
    margin-bottom: 6px
  .privacy-message
    font-size: 11px
    line-height: 15px
</style>
